import { tr } from '@/lib/i18n-core';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarPlus } from 'lucide-react';
import { useWorkingBranchId } from '@/lib/branch';
import { FormScreen } from '@/components/ui/form-screen';
import { BookAppointmentForm, type BookingDefaults } from './BookAppointmentForm';
import { renderPayerField } from './PayerPicker';

export function safeReturnTo(value: string | null, fallback: string): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : fallback;
}

export function BookAppointmentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branchId = useWorkingBranchId();
  const [dirty, setDirty] = useState(false);
  const onDirtyChange = useCallback((next: boolean) => setDirty(next), []);

  const returnTo = safeReturnTo(params.get('returnTo'), '/appointments');
  const departmentId = params.get('departmentId') || null;
  const defaults: BookingDefaults = {
    patientId: params.get('patientId') ?? '',
    doctorId: params.get('doctorId') ?? '',
    type: params.get('type') === 'follow_up' ? 'follow_up' : 'new',
    date: params.get('date') ?? undefined,
    slotAt: params.get('slotAt') ?? undefined,
  };

  const finish = () => {
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
    navigate(returnTo, { replace: true });
  };

  return (
    <FormScreen
      title={tr('opd', 'book_an_appointment')}
      description={tr('opd', 'pick_the_patient_the_doctor_and_a_free_slot')}
      icon={CalendarPlus}
      backTo={returnTo}
      dirty={dirty}
    >
      <BookAppointmentForm
        key={branchId}
        variant="screen"
        branchId={branchId}
        departmentId={departmentId}
        defaults={defaults}
        payerField={renderPayerField}
        onBooked={finish}
        onCancel={() => navigate(returnTo)}
        onDirtyChange={onDirtyChange}
      />
    </FormScreen>
  );
}
